import { accommodationRateIdZodSchema } from "@@/shared/acquire/accommodation-rate-id";
import { resourceIdZodSchema } from "@@/shared/acquire/resources/resource-id";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { entityStateZodSchema } from "@@/shared/entity-status";
import { productIdZodSchema } from "@@/shared/products/product-id";
import type {
    ChannexMappingId} from "@@/shared/providers/integrations/channex/channex-mapping-id";
import {
    channexMappingIdFactory,
    channexMappingIdZodSchema,
} from "@@/shared/providers/integrations/channex/channex-mapping-id";
import type { Except } from "type-fest";
import { z } from "zod";

const channexRoomMappingZodObject = z.object({
    _id: channexMappingIdZodSchema,
    _type: z.literal("CHANNEX_MAPPING"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,

    productId: productIdZodSchema,

    mappingType: z.literal("ROOM"),
    groupId: resourceIdZodSchema,
    channexRoomId: z.string(),
});

type ChannexRoomMapping = z.infer<typeof channexRoomMappingZodObject>;

type MappingFactoryParams = Except<
    ChannexRoomMapping,
    "_id" | "_createdAt" | "_type" | "_status" | "mappingType"
> & {
    _id?: ChannexMappingId;
    _status?: ChannexRoomMapping["_status"];
};
const channexRoomMappingFactory = (
    input: MappingFactoryParams,
): ChannexRoomMapping => {
    return {
        ...input,
        _id: input._id ?? channexMappingIdFactory(),
        _type: "CHANNEX_MAPPING",
        _createdAt: nowIsoAndUnix(),
        _status: input._status ?? "ACTIVE",
        mappingType: "ROOM",
    };
};

const channexRateMappingZodObject = z.object({
    _id: channexMappingIdZodSchema,
    _type: z.literal("CHANNEX_MAPPING"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,

    productId: productIdZodSchema,

    mappingType: z.literal("RATE"),
    rateId: accommodationRateIdZodSchema,
    channexRateId: z.string(),
});

type ChannexRateMapping = z.infer<typeof channexRateMappingZodObject>;

type RateMappingFactoryParams = Except<
    ChannexRateMapping,
    "_id" | "_createdAt" | "_type" | "_status" | "mappingType"
> & {
    _id?: ChannexMappingId;
    _status?: ChannexRateMapping["_status"];
};

const channexRateMappingFactory = (
    input: RateMappingFactoryParams,
): ChannexRateMapping => {
    return {
        ...input,
        _id: input._id ?? channexMappingIdFactory(),
        _type: "CHANNEX_MAPPING",
        _createdAt: nowIsoAndUnix(),
        _status: input._status ?? "ACTIVE",
        mappingType: "RATE",
    };
};

type ChannexMapping = ChannexRoomMapping | ChannexRateMapping;

export {
    channexRateMappingFactory,
    channexRateMappingZodObject,
    channexRoomMappingFactory,
    channexRoomMappingZodObject,
};

export type {
    ChannexMapping,
    ChannexRateMapping,
    ChannexRoomMapping,
    MappingFactoryParams,
    RateMappingFactoryParams,
};
